<template>
  <div class="edit-post-view">
    <!-- Header -->
    <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-6">
      <div class="d-flex align-center ga-3">
        <v-btn
          :to="{ name: 'MovieDetail', params: { id: $route.params.id } }"
          icon
          variant="tonal"
          size="small"
          aria-label="Back"
        >
          <v-icon size="20">mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <h1 class="text-h4 font-weight-bold">Edit Movie</h1>
          <p class="text-caption text-medium-emphasis mb-0">Update movie details in your collection</p>
        </div>
      </div>

      <!-- Actions -->
      <div v-if="!loading && !loadError" class="d-flex align-center flex-wrap ga-2">
        <v-btn
          :to="{ name: 'MovieDetail', params: { id: $route.params.id } }"
          variant="tonal"
          class="btn-modern"
        >
          Cancel
        </v-btn>

        <v-btn
          color="primary"
          class="btn-modern btn-glow-primary"
          :disabled="saving"
          :loading="saving"
          prepend-icon="mdi-content-save-check-outline"
          @click="saveMovie"
        >
          Save Changes
        </v-btn>
      </div>
    </div>

    <!-- Loading / Error States -->
    <div v-if="loading" class="text-center py-16">
      <v-progress-circular indeterminate color="primary" size="56" width="4" />
      <div class="text-body-2 text-medium-emphasis mt-4">Loading movie data...</div>
    </div>

    <v-alert v-else-if="loadError" type="error" class="mb-4" variant="tonal">{{ loadError }}</v-alert>

    <template v-else>
      <v-alert v-if="error" type="error" class="mb-4" closable variant="tonal" @click:close="error = ''">
        {{ error }}
      </v-alert>

      <!-- Main Edit Form -->
      <v-form ref="formRef">
        <v-row>
          <!-- Left Column: Poster -->
          <v-col cols="12" md="4" lg="3">
            <v-card class="glass-card pa-4 text-center sticky-poster-card">
              <div class="text-subtitle-2 font-weight-bold mb-3 text-left">Movie Poster</div>

              <div class="poster-dropzone mb-3">
                <v-img
                  v-if="posterPreview"
                  :src="posterPreview"
                  aspect-ratio="2/3"
                  cover
                  class="rounded-lg shadow-md"
                />
                <div v-else class="poster-empty-placeholder">
                  <v-icon size="48" color="primary" class="mb-2">mdi-image-outline</v-icon>
                  <div class="text-caption font-weight-bold">No Poster Image</div>
                </div>
              </div>

              <v-file-input
                v-model="imageFile"
                label="Replace Poster Image"
                accept="image/*"
                density="compact"
                hide-details
                prepend-icon=""
                prepend-inner-icon="mdi-upload"
                variant="outlined"
                class="mb-2"
              />

              <v-text-field
                v-model="movie.imageurl"
                label="Or Poster URL"
                density="compact"
                hide-details
                variant="outlined"
              />
            </v-card>
          </v-col>

          <!-- Right Column: Sections -->
          <v-col cols="12" md="8" lg="9">
            <!-- Section 1: Core Information -->
            <v-card class="glass-card pa-6 mb-6">
              <div class="d-flex align-center ga-2 mb-4">
                <v-icon color="primary" size="22">mdi-information-outline</v-icon>
                <h3 class="text-subtitle-1 font-weight-bold mb-0">Core Information</h3>
              </div>

              <v-row dense>
                <v-col cols="12" sm="8">
                  <v-text-field
                    v-model="movie.title"
                    label="Movie Title *"
                    density="comfortable"
                    :rules="[required]"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="movie.category"
                    :items="CATEGORIES"
                    label="Category *"
                    density="comfortable"
                    :rules="[required]"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="movie.original"
                    label="Original Title (Optional)"
                    density="comfortable"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="movie.director"
                    label="Director"
                    density="comfortable"
                  />
                </v-col>

                <v-col cols="6" sm="3">
                  <v-text-field
                    v-model="movie.year"
                    label="Release Year"
                    density="comfortable"
                  />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field
                    v-model="movie.duration"
                    label="Runtime"
                    density="comfortable"
                  />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-select
                    v-model="movie.language"
                    :items="LANGUAGES"
                    label="Language"
                    density="comfortable"
                  />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-select
                    v-model="movie.quality"
                    :items="QUALITIES"
                    label="Video Quality"
                    density="comfortable"
                  />
                </v-col>

                <!-- Rating -->
                <v-col cols="12" class="mt-2">
                  <div class="d-flex align-center ga-3">
                    <span class="text-caption font-weight-bold text-medium-emphasis">Your Rating:</span>
                    <v-rating
                      v-model="ratingNumber"
                      half-increments
                      density="comfortable"
                      color="amber"
                      active-color="amber"
                    />
                    <span class="text-body-2 font-weight-bold">{{ ratingNumber }} / 5</span>
                  </div>
                </v-col>
              </v-row>
            </v-card>

            <!-- Section 2: Synopsis & Media -->
            <v-card class="glass-card pa-6 mb-6">
              <div class="d-flex align-center ga-2 mb-4">
                <v-icon color="secondary" size="22">mdi-text-box-outline</v-icon>
                <h3 class="text-subtitle-1 font-weight-bold mb-0">Synopsis & Trailer</h3>
              </div>

              <v-row dense>
                <v-col cols="12">
                  <v-textarea
                    v-model="movie.synopsis"
                    label="Synopsis / Storyline"
                    rows="3"
                    auto-grow
                    density="comfortable"
                    variant="outlined"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="movie.youtubeId"
                    label="YouTube Trailer Video ID"
                    density="comfortable"
                    prepend-inner-icon="mdi-youtube"
                    variant="outlined"
                  />
                </v-col>
              </v-row>
            </v-card>

            <!-- Section 3: Cast -->
            <v-card class="glass-card pa-6 mb-6">
              <div class="d-flex align-center ga-2 mb-4">
                <v-icon color="accent" size="22">mdi-account-multiple-outline</v-icon>
                <h3 class="text-subtitle-1 font-weight-bold mb-0">Cast Members</h3>
              </div>

              <v-text-field
                v-model="movie.casting"
                label="Cast Overview (Comma-separated)"
                density="comfortable"
                class="mb-4"
              />

              <div class="text-caption font-weight-bold text-medium-emphasis mb-3">
                Key Actor Avatars
              </div>
              <v-row dense>
                <v-col
                  v-for="n in 6"
                  :key="n"
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <v-card variant="tonal" class="pa-3 rounded-lg mb-2">
                    <div class="d-flex align-center ga-2 mb-2">
                      <v-avatar size="36" color="primary" class="rounded-lg">
                        <v-img v-if="movie[`actorpix${n}`]" :src="movie[`actorpix${n}`]" cover />
                        <v-icon v-else size="20">mdi-account</v-icon>
                      </v-avatar>
                      <div class="text-caption font-weight-bold">Actor {{ n }}</div>
                    </div>
                    <v-text-field
                      v-model="movie[`actorname${n}`]"
                      :label="`Actor ${n} Name`"
                      density="compact"
                      hide-details
                      class="mb-2"
                    />
                    <v-text-field
                      v-model="movie[`actorpix${n}`]"
                      label="Photo URL"
                      density="compact"
                      hide-details
                    />
                  </v-card>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </template>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { CATEGORIES, LANGUAGES, QUALITIES } from '@/config/movies'
import MoviesAPI from '@/services/movies'
import { appendMovieFormData, defaultMovieForm } from '@/utils/movieForm'
import { apiErrorMessage } from '@/utils/formValidate'

export default {
  name: 'EditPost',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const formRef = ref(null)
    const movie = reactive(defaultMovieForm())
    const oldImage = ref('')
    const imageFile = ref(null)
    const loading = ref(true)
    const saving = ref(false)
    const error = ref('')
    const loadError = ref('')

    const required = (value) => !!value || 'This field is required'

    const ratingNumber = computed({
      get: () => Number(movie.rating) || 0,
      set: (value) => {
        movie.rating = String(value)
      },
    })

    const posterPreview = computed(() => {
      const file = imageFile.value
      if (file) {
        const selected = Array.isArray(file) ? file[0] : file
        if (selected) return URL.createObjectURL(selected)
      }
      if (movie.imageurl) return movie.imageurl
      if (movie.image) return `/uploads/${movie.image}`
      return ''
    })

    const load = async () => {
      loading.value = true
      loadError.value = ''
      try {
        const { data } = await MoviesAPI.getPostByID(route.params.id)
        Object.assign(movie, { ...defaultMovieForm(), ...data })
        oldImage.value = data.image || ''
      } catch (err) {
        loadError.value = apiErrorMessage(err, 'Movie not found')
      } finally {
        loading.value = false
      }
    }

    const saveMovie = async () => {
      error.value = ''
      const title = String(movie.title || '').trim()
      const category = String(movie.category || '').trim()

      if (!title || !category) {
        error.value = 'Title and category are required before saving.'
        return
      }

      movie.title = title
      movie.category = category
      saving.value = true
      try {
        const formData = new FormData()
        appendMovieFormData(formData, movie)
        formData.append('old_image', oldImage.value || '')
        if (imageFile.value) {
          const file = Array.isArray(imageFile.value) ? imageFile.value[0] : imageFile.value
          if (file) formData.append('image', file)
        }
        await MoviesAPI.updatePost(route.params.id, formData)
        router.push({ name: 'MovieDetail', params: { id: route.params.id } })
      } catch (err) {
        error.value = apiErrorMessage(err, 'Failed to update movie')
      } finally {
        saving.value = false
      }
    }

    onMounted(load)

    return {
      formRef,
      movie,
      imageFile,
      loading,
      saving,
      error,
      loadError,
      CATEGORIES,
      LANGUAGES,
      QUALITIES,
      required,
      ratingNumber,
      posterPreview,
      saveMovie,
    }
  },
}
</script>

<style scoped>
.edit-post-view {
  max-width: 1240px;
  margin: 0 auto;
}

.sticky-poster-card {
  position: sticky;
  top: 80px;
}

.poster-dropzone {
  position: relative;
  aspect-ratio: 2 / 3;
  border-radius: 12px;
  overflow: hidden;
}

.poster-empty-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  border: 2px dashed rgba(128, 128, 128, 0.3);
  border-radius: 12px;
  padding: 16px;
  background: rgba(128, 128, 128, 0.05);
}
</style>

